/**
 * PoolAvailabilityPanel — Q1 de l'audit CD31 : « combien de véhicules
 * disponibles, réservés, sortis, bloqués ? », comptés, pas listés.
 *
 * Complète `PoolCustodyPanel` (ce qui est DEHORS maintenant) par ce qui NE
 * L'EST PAS : quatre compteurs disjoints sur une fenêtre choisie, chacun
 * ouvrant sa liste — plaque, modèle, U.T., et le ou les motifs en français
 * (`docs/labo/2026-09-13_AUDIT_CAPACITE_METIER_CD31.md` lot 1.1).
 *
 * ⚠️ Un véhicule compte dans UNE seule catégorie (le serveur tranche, voir
 * `availability_service.mjs`) : les quatre compteurs se somment sans double
 * comptage, contrairement à « dehors »/« en retard » du panneau garde.
 *
 * Rendu comme contenu d'onglet « Planning » (mode Compteurs) de l'écran
 * « Parc partagé » — plus de carte repliable à chevron ici, le parent la
 * porte. À 900 véhicules / 1 000 conducteurs, deux tableaux de synthèse
 * (« Par U.T. », « Par catégorie ») évitent d'ouvrir une longue liste plate
 * pour une question qui se répond en un coup d'œil, U.T. par U.T.
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2, Clock, LogOut, ShieldOff, ChevronDown, ChevronRight, Car, Share2, Download,
} from 'lucide-react';
import { poolAvailabilityAPI } from '../../api/pool.js';
import { useFormat } from '../../i18n/format.js';
import PoolProvisionDialog from './PoolProvisionDialog.jsx';

/**
 * Lot 3.3 — deux états de plus, et toujours UN SEUL par véhicule (le serveur
 * tranche, `availability_service.mjs`) : les compteurs se somment sans double
 * comptage, mise à disposition comprise.
 *   `provided_in`  — prêté par une autre unité, RÉSERVABLE ici ;
 *   `provided_out` — parti chez une autre unité (jamais « disponible » :
 *                    il n'est pas là).
 */
const STATES = ['available', 'provided_in', 'reserved', 'out', 'provided_out', 'blocked'];

const STATE_ICON = {
  available: CheckCircle2,
  provided_in: Download,
  reserved: Clock,
  out: LogOut,
  provided_out: Share2,
  blocked: ShieldOff,
};

const STATE_COLOR = {
  available: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  provided_in: 'text-teal-700 bg-teal-50 border-teal-200',
  reserved: 'text-amber-700 bg-amber-50 border-amber-200',
  out: 'text-blue-700 bg-blue-50 border-blue-200',
  provided_out: 'text-violet-700 bg-violet-50 border-violet-200',
  blocked: 'text-red-700 bg-red-50 border-red-200',
};

/** Sentinelle locale pour « catégorie = aucune » : le serveur ne sait pas
 * filtrer sur `vehicle_class IS NULL` (un `vehicleClass` faux revient à ne
 * pas filtrer, voir `availability_service.mjs:144`) — on filtre donc ce cas
 * côté client plutôt que d'envoyer une requête qui rendrait tout le monde. */
const NO_CLASS = '__none__';

/** Fenêtre par défaut : maintenant → +4 h, en ISO 8601 avec décalage explicite. */
function defaultWindow() {
  const from = new Date();
  const to = new Date(from.getTime() + 4 * 60 * 60_000);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** `<input type="datetime-local">` ne connaît pas les fuseaux : aller-retour local. */
function toLocalInputValue(iso) {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInputValue(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Un motif d'indisponibilité, en français, avec sa date quand elle existe. */
function ReasonLine({ reason, t, fmt }) {
  if (reason.kind === 'blocking_defect') {
    return (
      <li className="text-xs text-gray-600">
        {t('poolAvailability.reason.blockingDefect', {
          description: reason.description || t('poolAvailability.reason.noDescription'),
          when: reason.reported_at ? fmt.dateTime(reason.reported_at) : '—',
        })}
      </li>
    );
  }
  // Lot 3.3 — la mise à disposition se DIT en clair : chez qui, et jusqu'à quand.
  // Sans la date de fin, l'utilisateur téléphone à l'unité voisine pour l'apprendre.
  if (reason.kind === 'provided_out' || reason.kind === 'provided_in') {
    const label = reason.kind === 'provided_out'
      ? 'poolAvailability.reason.providedOut'
      : 'poolAvailability.reason.providedIn';
    return (
      <li className="text-xs text-gray-600">
        {t(label, {
          site: reason.kind === 'provided_out'
            ? (reason.site_name || t('poolAvailability.site.none'))
            : (reason.home_site_name || t('poolAvailability.site.none')),
          until: reason.to ? fmt.date(reason.to) : '—',
        })}
      </li>
    );
  }
  const key = reason.kind === 'out' ? 'poolAvailability.reason.out' : 'poolAvailability.reason.reserved';
  return (
    <li className="text-xs text-gray-600">
      {t(key, {
        from: fmt.dateTime(reason.planned_start),
        to: fmt.time(reason.planned_end),
      })}
    </li>
  );
}

function VehicleRow({ vehicle, t, fmt, onProvision }) {
  // On ne propose de PRÊTER que ce qu'on a sous la main : un véhicule déjà
  // sorti de son unité (`provided_out`) ou déjà prêté à la nôtre (`provided_in`)
  // ne se re-prête pas — le serveur le refuserait, autant ne pas le proposer.
  const canProvision = Boolean(onProvision)
    && vehicle.state !== 'provided_out'
    && vehicle.state !== 'provided_in';

  return (
    <li className="flex flex-col gap-1 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <p className="flex items-center gap-2 font-medium text-gray-900">
          <Car className="h-4 w-4 shrink-0 text-gray-400" />
          <span className="truncate">
            {vehicle.license_plate || t('poolAvailability.vehicle.unknownPlate')}
          </span>
          <span className="text-gray-400">·</span>
          <span className="truncate text-gray-700">
            {[vehicle.make, vehicle.model].filter(Boolean).join(' ') || t('poolAvailability.vehicle.unknownModel')}
          </span>
        </p>
        {vehicle.vehicle_class && (
          <p className="mt-0.5 text-xs text-gray-500">{t('poolAvailability.vehicle.class', { class: vehicle.vehicle_class })}</p>
        )}
        {vehicle.reasons?.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {vehicle.reasons.map((r, i) => (
              <ReasonLine key={i} reason={r} t={t} fmt={fmt} />
            ))}
          </ul>
        )}
      </div>
      {canProvision && (
        <button
          type="button"
          onClick={() => onProvision(vehicle)}
          className="shrink-0 self-start rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          <span className="flex items-center gap-1.5">
            <Share2 className="h-3.5 w-3.5" />
            {t('pool.provision.actions.open')}
          </span>
        </button>
      )}
    </li>
  );
}

/**
 * Regroupe une liste de véhicules par U.T., triés par nom (« Sans U.T. » en
 * dernier). Pur : ne connaît rien de l'i18n, `site_name: null` pour le
 * groupe sans U.T. — c'est au composant de rendre le libellé.
 */
export function groupVehiclesBySite(vehicles, sites) {
  const nameById = new Map((sites || []).map((s) => [String(s.id), s.site_name]));
  const groups = new Map();
  for (const v of vehicles || []) {
    const hasSite = v.site_id !== null && v.site_id !== undefined;
    const key = hasSite ? String(v.site_id) : '__none__';
    if (!groups.has(key)) {
      groups.set(key, {
        site_id: hasSite ? v.site_id : null,
        site_name: hasSite ? (nameById.get(key) ?? null) : null,
        vehicles: [],
      });
    }
    groups.get(key).vehicles.push(v);
  }
  return Array.from(groups.values()).sort((a, b) => {
    if (a.site_name === null) return b.site_name === null ? 0 : 1;
    if (b.site_name === null) return -1;
    return a.site_name.localeCompare(b.site_name, 'fr');
  });
}

/** Même tri « nom, sans U.T. en dernier » pour les lignes agrégées du serveur (`by_site`). */
function sortBySiteName(rows) {
  return [...rows].sort((a, b) => {
    if (a.site_name == null) return b.site_name == null ? 0 : 1;
    if (b.site_name == null) return -1;
    return a.site_name.localeCompare(b.site_name, 'fr');
  });
}

/** Tri alphabétique des catégories, « sans catégorie » en dernier. */
function sortByClassName(rows) {
  return [...rows].sort((a, b) => {
    if (a.vehicle_class == null) return b.vehicle_class == null ? 0 : 1;
    if (b.vehicle_class == null) return -1;
    return a.vehicle_class.localeCompare(b.vehicle_class, 'fr');
  });
}

/** En-tête + corps d'un tableau de synthèse compact (Par U.T. / Par catégorie). */
function SummaryTable({ titleKey, rows, labelFor, isActive, onRowClick, t }) {
  return (
    <div className="mt-4">
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">{t(titleKey)}</p>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-1.5 text-left font-medium text-gray-500"> </th>
              <th className="px-3 py-1.5 text-right font-medium text-gray-500">{t('poolAvailability.table.total')}</th>
              {STATES.map((s) => (
                <th key={s} className="px-3 py-1.5 text-right font-medium text-gray-500">{t(`poolAvailability.state.${s}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row, i) => {
              const clickable = onRowClick && onRowClick.enabled(row);
              const active = isActive ? isActive(row) : false;
              return (
                <tr
                  key={i}
                  onClick={clickable ? () => onRowClick.handler(row) : undefined}
                  className={`${clickable ? 'cursor-pointer hover:bg-gray-50' : ''} ${active ? 'bg-blue-50' : ''}`}
                >
                  <td className="px-3 py-1.5 text-gray-800">{labelFor(row)}</td>
                  <td className="px-3 py-1.5 text-right text-gray-500">{row.total}</td>
                  {STATES.map((s) => (
                    <td key={s} className="px-3 py-1.5 text-right text-gray-800">{row[s] ?? 0}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PoolAvailabilityPanel({ siteId = null, sites = [], search = '', onPickSite }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  const [window_, setWindow] = useState(defaultWindow);
  const [vehicleClass, setVehicleClass] = useState('');
  const [expandedState, setExpandedState] = useState(null);
  const [manualGroups, setManualGroups] = useState({});
  // Lot 3.3 — le véhicule qu'on est en train de mettre à disposition, ou `null`.
  const [provisioning, setProvisioning] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['pool-availability', siteId, window_.from, window_.to, vehicleClass],
    queryFn: () => poolAvailabilityAPI.get({
      from: window_.from,
      to: window_.to,
      siteId: siteId && siteId !== 'all' ? siteId : undefined,
      // NO_CLASS ne se traduit pas côté serveur (voir la sentinelle ci-dessus) : on ne filtre rien
      // ici et on retire les véhicules catégorisés après coup.
      vehicleClass: vehicleClass && vehicleClass !== NO_CLASS ? vehicleClass : undefined,
    }),
    refetchInterval: 60_000,
  });

  const payload = data?.data || data || {};
  const summary = payload.summary || { total: 0, available: 0, reserved: 0, out: 0, blocked: 0 };
  const bySite = payload.by_site || [];
  const byClass = payload.by_class || [];
  const rawVehicles = payload.vehicles || [];

  const scopedVehicles = useMemo(
    () => (vehicleClass === NO_CLASS ? rawVehicles.filter((v) => !v.vehicle_class) : rawVehicles),
    [rawVehicles, vehicleClass]
  );

  const searchLower = search.trim().toLowerCase();
  const searchActive = searchLower.length >= 2;

  const filteredVehicles = useMemo(() => {
    let list = expandedState ? scopedVehicles.filter((v) => v.state === expandedState) : [];
    if (searchActive) {
      list = list.filter((v) => [v.license_plate, v.make, v.model]
        .filter(Boolean)
        .some((f) => f.toLowerCase().includes(searchLower)));
    }
    return list;
  }, [scopedVehicles, expandedState, searchActive, searchLower]);

  const groups = useMemo(
    () => (siteId === 'all' ? groupVehiclesBySite(filteredVehicles, sites) : null),
    [siteId, filteredVehicles, sites]
  );

  const groupsDefaultExpanded = searchActive || filteredVehicles.length <= 40;
  const isGroupExpanded = (key) => (key in manualGroups ? manualGroups[key] : groupsDefaultExpanded);
  const toggleGroup = (key) => setManualGroups((prev) => ({ ...prev, [key]: !isGroupExpanded(key) }));

  const showBySiteTable = siteId === 'all' && bySite.length >= 2;
  const showByClassTable = byClass.length >= 2;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600">{t('poolAvailability.window.from')}</span>
          <input
            type="datetime-local"
            value={toLocalInputValue(window_.from)}
            onChange={(e) => {
              const from = fromLocalInputValue(e.target.value);
              if (from) setWindow((w) => ({ ...w, from }));
            }}
            className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600">{t('poolAvailability.window.to')}</span>
          <input
            type="datetime-local"
            value={toLocalInputValue(window_.to)}
            onChange={(e) => {
              const to = fromLocalInputValue(e.target.value);
              if (to) setWindow((w) => ({ ...w, to }));
            }}
            className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
          />
        </label>
        {byClass.length > 0 && (
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-600">{t('poolAvailability.window.vehicleClass')}</span>
            <select
              value={vehicleClass}
              onChange={(e) => setVehicleClass(e.target.value)}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="">{t('poolAvailability.window.allClasses')}</option>
              {byClass.map((c) => (
                <option key={c.vehicle_class ?? NO_CLASS} value={c.vehicle_class ?? NO_CLASS}>
                  {c.vehicle_class || t('poolAvailability.class.none')}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {error && (
        <p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {t('poolAvailability.errors.loadFailed', { message: error.message })}
        </p>
      )}

      {isLoading ? (
        <p className="py-6 text-center text-sm text-gray-500">{t('poolAvailability.loading')}</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {STATES.map((state) => {
              const Icon = STATE_ICON[state];
              const isExpanded = expandedState === state;
              return (
                <button
                  key={state}
                  onClick={() => setExpandedState(isExpanded ? null : state)}
                  className={`flex flex-col items-start gap-1 rounded-lg border px-3 py-2.5 text-left transition ${STATE_COLOR[state]} ${isExpanded ? 'ring-2 ring-offset-1' : ''}`}
                >
                  <span className="flex items-center gap-1.5 text-xs font-medium">
                    <Icon className="h-3.5 w-3.5" />
                    {t(`poolAvailability.state.${state}`)}
                  </span>
                  <span className="text-2xl font-bold">{summary[state] ?? 0}</span>
                </button>
              );
            })}
          </div>

          {showBySiteTable && (
            <SummaryTable
              titleKey="poolAvailability.table.bySiteTitle"
              rows={sortBySiteName(bySite)}
              labelFor={(row) => row.site_name || t('poolAvailability.site.none')}
              onRowClick={{
                enabled: (row) => Boolean(onPickSite) && row.site_id !== null && row.site_id !== undefined,
                handler: (row) => onPickSite(row.site_id),
              }}
              t={t}
            />
          )}

          {showByClassTable && (
            <SummaryTable
              titleKey="poolAvailability.table.byClassTitle"
              rows={sortByClassName(byClass)}
              labelFor={(row) => row.vehicle_class || t('poolAvailability.class.none')}
              isActive={(row) => vehicleClass === (row.vehicle_class || NO_CLASS)}
              onRowClick={{
                enabled: () => true,
                handler: (row) => {
                  const value = row.vehicle_class || NO_CLASS;
                  setVehicleClass((prev) => (prev === value ? '' : value));
                },
              }}
              t={t}
            />
          )}

          {expandedState && (
            <div className="mt-4 border-t border-gray-100 pt-3">
              <p className="mb-2 text-sm font-medium text-gray-700">
                {t(`poolAvailability.state.${expandedState}`)} — {filteredVehicles.length}
              </p>
              {filteredVehicles.length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-500">{t('poolAvailability.empty')}</p>
              ) : groups ? (
                <div className="space-y-3">
                  {groups.map((group) => {
                    const key = group.site_id ?? '__none__';
                    const expanded = isGroupExpanded(key);
                    return (
                      <div key={key} className="rounded-lg border border-gray-100">
                        <button
                          onClick={() => toggleGroup(key)}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left"
                          aria-expanded={expanded}
                        >
                          {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                          <span className="text-sm font-medium text-gray-800">{group.site_name || t('poolAvailability.site.none')}</span>
                          <span className="text-xs text-gray-500">{t('poolAvailability.group.count', { count: group.vehicles.length })}</span>
                        </button>
                        {expanded && (
                          <ul className="divide-y divide-gray-100 px-3">
                            {group.vehicles.map((v) => (
                              <VehicleRow key={v.vehicle_id} vehicle={v} t={t} fmt={fmt} onProvision={setProvisioning} />
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {filteredVehicles.map((v) => (
                    <VehicleRow key={v.vehicle_id} vehicle={v} t={t} fmt={fmt} onProvision={setProvisioning} />
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      {/* Lot 3.3 — mettre le véhicule à disposition d'une autre unité. Le
          dialogue ne décide de rien : le serveur refuse et NOMME les conflits. */}
      {provisioning && (
        <PoolProvisionDialog
          vehicle={provisioning}
          sites={sites}
          onClose={() => setProvisioning(null)}
          onProvisioned={() => setProvisioning(null)}
        />
      )}
    </div>
  );
}

export default PoolAvailabilityPanel;
